// Mercapoli - Dashboard Comercio Completo
import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Package, DollarSign, Clock, CheckCircle, XCircle,
  Store, LogOut, TrendingUp, Wallet, 
  Calendar, ChevronRight, Coins, AlertTriangle, RefreshCw, Shield,
  BarChart3, ShoppingBag, Star, Tag, FileText, Menu as MenuIcon, X,
  Globe
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import { comercio as comercioApi, pedidos as pedidosApi } from '../../lib/api';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_mercadelivery-1/artifacts/5y038jeg_icono_mercapoli.png";

const MerchantDashboard = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [dashboard, setDashboard] = useState(null);
  const [estadisticas, setEstadisticas] = useState(null);
  const [pedidos, setPedidos] = useState([]);
  const [resumenVentas, setResumenVentas] = useState(null);
  const [activeTab, setActiveTab] = useState('resumen');
  const [showAceptarComision, setShowAceptarComision] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [periodoVentas, setPeriodoVentas] = useState('semana');

  useEffect(() => {
    loadData();
    const interval = setInterval(loadData, 30000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    loadResumenVentas();
  }, [periodoVentas]);

  const loadData = async () => {
    try {
      const [dashData, pedidosData, statsData] = await Promise.all([
        comercioApi.getDashboard(),
        comercioApi.getPedidos(),
        comercioApi.getEstadisticas()
      ]);
      setDashboard(dashData);
      setPedidos(pedidosData);
      setEstadisticas(statsData);
    } catch (error) {
      console.error('Error:', error);
    }
    setLoading(false);
  };

  const loadResumenVentas = async () => {
    try {
      const data = await comercioApi.getResumenVentas(periodoVentas);
      setResumenVentas(data);
    } catch (error) {
      console.error('Error cargando ventas:', error);
    }
  };

  const updateEstado = async (pedidoId, nuevoEstado) => {
    try {
      await pedidosApi.updateEstado(pedidoId, nuevoEstado);
      toast.success(`Pedido ${nuevoEstado === 'cancelado' ? 'cancelado' : 'actualizado'}`);
      loadData();
    } catch (error) {
      toast.error('Error al actualizar pedido');
    }
  };

  const aceptarComision = async () => {
    try {
      await comercioApi.aceptarComision();
      toast.success('¡Comisión aceptada! Ya puedes recibir pedidos.');
      setShowAceptarComision(false);
      loadData();
    } catch (error) {
      toast.error('Error al aceptar comisión');
    }
  };

  const simbolo = estadisticas?.simbolo || dashboard?.simbolo || 'Q';
  const formatMoney = (amount) => `${simbolo}${(amount || 0).toLocaleString('es-GT', { minimumFractionDigits: 2 })}`;

  const getStatusColor = (status) => {
    const colors = {
      pendiente: 'bg-yellow-100 text-yellow-800',
      aceptado: 'bg-blue-100 text-blue-800',
      preparando: 'bg-purple-100 text-purple-800',
      listo: 'bg-green-100 text-green-800',
      en_camino: 'bg-cyan-100 text-cyan-800',
      entregado: 'bg-emerald-100 text-emerald-800',
      cancelado: 'bg-red-100 text-red-800',
    };
    return colors[status] || 'bg-gray-100';
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const pedidosPendientes = pedidos.filter(p => p.estado === 'pendiente');
  const pedidosActivos = pedidos.filter(p => ['aceptado', 'preparando', 'listo'].includes(p.estado));

  const menuItems = [
    { icon: BarChart3, label: 'Dashboard', path: '/merchant', active: true },
    { icon: ShoppingBag, label: 'Productos', path: '/merchant/productos' },
    { icon: Globe, label: 'Menú en Línea', path: '/merchant/menu-widget' },
    { icon: Tag, label: 'Promociones', path: '/merchant/promociones' },
    { icon: Star, label: 'Reviews', path: '/merchant/reviews' },
    { icon: FileText, label: 'Ventas', path: '/merchant/ventas' },
    { icon: Wallet, label: 'Liquidaciones', path: '/merchant/liquidaciones' },
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex">
      {/* Sidebar Mobile Overlay */}
      {sidebarOpen && (
        <div className="fixed inset-0 bg-black/50 z-40 lg:hidden" onClick={() => setSidebarOpen(false)} />
      )}

      {/* Sidebar */}
      <aside className={`fixed lg:static inset-y-0 left-0 z-50 w-64 bg-card border-r transform transition-transform duration-200 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}`}>
        <div className="flex items-center gap-3 p-4 border-b">
          <img src={LOGO_URL} alt="Mercapoli" className="h-10 w-10" />
          <div>
            <h1 className="font-heading font-bold text-lg">Mercapoli</h1>
            <p className="text-xs text-muted-foreground">Panel Comercio</p>
          </div>
          <Button variant="ghost" size="icon" className="lg:hidden ml-auto" onClick={() => setSidebarOpen(false)}>
            <X className="h-5 w-5" />
          </Button>
        </div>

        <nav className="p-4 space-y-1">
          {menuItems.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              className={`flex items-center gap-3 px-3 py-2 rounded-lg transition-colors ${
                item.active ? 'bg-brand-primary text-white' : 'hover:bg-muted'
              }`}
            >
              <item.icon className="h-5 w-5" />
              <span>{item.label}</span>
            </Link>
          ))}
        </nav>

        <div className="absolute bottom-0 left-0 right-0 p-4 border-t">
          <div className="flex items-center gap-3 mb-3">
            <Store className="h-8 w-8 text-brand-primary" />
            <div className="flex-1 min-w-0">
              <p className="font-medium truncate">{dashboard?.comercio?.nombre || 'Mi Comercio'}</p>
              <p className="text-xs text-muted-foreground">{user?.email}</p>
            </div>
          </div>
          <Button variant="outline" className="w-full" onClick={handleLogout}>
            <LogOut className="h-4 w-4 mr-2" />
            Cerrar Sesión
          </Button>
        </div>
      </aside>

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-h-screen">
        {/* Header */}
        <header className="sticky top-0 z-30 bg-background/95 backdrop-blur border-b">
          <div className="flex items-center justify-between h-14 px-4">
            <div className="flex items-center gap-3">
              <Button variant="ghost" size="icon" className="lg:hidden" onClick={() => setSidebarOpen(true)}>
                <MenuIcon className="h-5 w-5" />
              </Button>
              <h2 className="font-heading font-bold">Dashboard</h2>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="ghost" size="icon" onClick={loadData}>
                <RefreshCw className="h-5 w-5" />
              </Button>
              {pedidosPendientes.length > 0 && (
                <Badge className="bg-red-500">{pedidosPendientes.length} nuevos</Badge>
              )}
            </div>
          </div>
        </header>

        <main className="flex-1 p-4 lg:p-6 overflow-auto">
          {/* Alerta de comisión pendiente */}
          {dashboard?.comercio && !dashboard.comercio.comision_aceptada && (
            <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} className="mb-6">
              <Card className="border-yellow-500 bg-yellow-50 dark:bg-yellow-950/20">
                <CardContent className="p-4">
                  <div className="flex items-start gap-3">
                    <AlertTriangle className="h-6 w-6 text-yellow-600 flex-shrink-0" />
                    <div className="flex-1">
                      <p className="font-bold text-yellow-800">Acepta la comisión para empezar</p>
                      <p className="text-sm text-yellow-700 mt-1">
                        Comisión negociada: <span className="font-bold">{dashboard.comercio.comision_porcentaje}%</span>
                      </p>
                      <Button className="mt-3 bg-yellow-600 hover:bg-yellow-700" onClick={() => setShowAceptarComision(true)}>
                        <Shield className="h-4 w-4 mr-2" /> Aceptar Comisión
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          )}

          {/* Stats Grid */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <Card className="bg-gradient-to-br from-brand-primary to-brand-secondary text-white">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm opacity-80">Ventas Hoy</p>
                    <p className="text-2xl font-bold">{formatMoney(estadisticas?.ventas_hoy || 0)}</p>
                  </div>
                  <DollarSign className="h-8 w-8 opacity-50" />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Pedidos Hoy</p>
                    <p className="text-2xl font-bold">{estadisticas?.pedidos?.hoy || 0}</p>
                  </div>
                  <Package className="h-8 w-8 text-brand-primary opacity-50" />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Productos Activos</p>
                    <p className="text-2xl font-bold">{estadisticas?.productos?.activos || 0}</p>
                  </div>
                  <ShoppingBag className="h-8 w-8 text-green-500 opacity-50" />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Calificación</p>
                    <p className="text-2xl font-bold flex items-center gap-1">
                      <Star className="h-5 w-5 text-yellow-500 fill-yellow-500" />
                      {estadisticas?.calificacion?.promedio?.toFixed(1) || '5.0'}
                    </p>
                  </div>
                  <span className="text-xs text-muted-foreground">
                    {estadisticas?.calificacion?.total_reviews || 0} reviews
                  </span>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Resumen Ventas */}
          {resumenVentas && (
            <Card className="mb-6">
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg">Resumen de Ventas</CardTitle>
                  <div className="flex gap-1">
                    {['hoy', 'semana', 'mes'].map((p) => (
                      <Button
                        key={p}
                        variant={periodoVentas === p ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => setPeriodoVentas(p)}
                      >
                        {p.charAt(0).toUpperCase() + p.slice(1)}
                      </Button>
                    ))}
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                  <div className="text-center p-3 bg-muted rounded-lg">
                    <p className="text-2xl font-bold text-green-600">{formatMoney(resumenVentas.total_ventas)}</p>
                    <p className="text-xs text-muted-foreground">Ventas Brutas</p>
                  </div>
                  <div className="text-center p-3 bg-muted rounded-lg">
                    <p className="text-2xl font-bold">{resumenVentas.total_pedidos}</p>
                    <p className="text-xs text-muted-foreground">Pedidos</p>
                  </div>
                  <div className="text-center p-3 bg-muted rounded-lg">
                    <p className="text-2xl font-bold text-red-500">-{formatMoney(resumenVentas.comision_total)}</p>
                    <p className="text-xs text-muted-foreground">Comisión ({resumenVentas.comision_porcentaje}%)</p>
                  </div>
                  <div className="text-center p-3 bg-muted rounded-lg">
                    <p className="text-2xl font-bold text-brand-primary">{formatMoney(resumenVentas.neto_estimado)}</p>
                    <p className="text-xs text-muted-foreground">Neto Estimado</p>
                  </div>
                </div>
                {resumenVentas.comparacion_periodo_anterior !== 0 && (
                  <div className="mt-4 flex items-center justify-center gap-2 text-sm">
                    {resumenVentas.comparacion_periodo_anterior > 0 ? (
                      <>
                        <TrendingUp className="h-4 w-4 text-green-500" />
                        <span className="text-green-500">+{resumenVentas.comparacion_periodo_anterior.toFixed(1)}%</span>
                      </>
                    ) : (
                      <>
                        <TrendingUp className="h-4 w-4 text-red-500 rotate-180" />
                        <span className="text-red-500">{resumenVentas.comparacion_periodo_anterior.toFixed(1)}%</span>
                      </>
                    )}
                    <span className="text-muted-foreground">vs periodo anterior</span>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Tabs de Pedidos */}
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="grid w-full grid-cols-2 mb-4">
              <TabsTrigger value="resumen" className="flex items-center gap-2">
                <Package className="h-4 w-4" />
                Pedidos Activos ({pedidosPendientes.length + pedidosActivos.length})
              </TabsTrigger>
              <TabsTrigger value="historial" className="flex items-center gap-2">
                <Clock className="h-4 w-4" />
                Historial
              </TabsTrigger>
            </TabsList>

            <TabsContent value="resumen">
              {pedidosPendientes.length === 0 && pedidosActivos.length === 0 ? (
                <Card className="text-center py-12">
                  <CardContent>
                    <Package className="h-12 w-12 mx-auto mb-4 text-muted-foreground opacity-50" />
                    <p className="text-muted-foreground">No hay pedidos pendientes</p>
                  </CardContent>
                </Card>
              ) : (
                <div className="grid gap-4 md:grid-cols-2">
                  {[...pedidosPendientes, ...pedidosActivos].map((pedido) => (
                    <Card key={pedido.pedido_id} className={pedido.estado === 'pendiente' ? 'border-yellow-500 border-2' : ''}>
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between mb-3">
                          <div>
                            <p className="font-bold">#{pedido.pedido_id.slice(-6)}</p>
                            <p className="text-xs text-muted-foreground">
                              {new Date(pedido.created_at).toLocaleString('es-GT')}
                            </p>
                          </div>
                          <Badge className={getStatusColor(pedido.estado)}>{pedido.estado}</Badge>
                        </div>

                        <div className="bg-muted p-3 rounded-lg mb-3 text-sm">
                          {pedido.items?.slice(0, 3).map((item, idx) => (
                            <div key={idx} className="flex justify-between">
                              <span>{item.cantidad}x {item.nombre}</span>
                              <span>{formatMoney(item.subtotal)}</span>
                            </div>
                          ))}
                          {pedido.items?.length > 3 && (
                            <p className="text-muted-foreground mt-1">+{pedido.items.length - 3} más...</p>
                          )}
                        </div>

                        <div className="flex justify-between items-center mb-3">
                          <span className="text-sm text-muted-foreground">Total:</span>
                          <span className="font-bold">{formatMoney(pedido.total)}</span>
                        </div>

                        {pedido.estado === 'pendiente' && (
                          <div className="flex gap-2">
                            <Button className="flex-1 bg-green-500 hover:bg-green-600" onClick={() => updateEstado(pedido.pedido_id, 'aceptado')}>
                              <CheckCircle className="h-4 w-4 mr-1" /> Aceptar
                            </Button>
                            <Button variant="outline" className="border-red-500 text-red-500" onClick={() => updateEstado(pedido.pedido_id, 'cancelado')}>
                              <XCircle className="h-4 w-4" />
                            </Button>
                          </div>
                        )}
                        {pedido.estado === 'aceptado' && (
                          <Button className="w-full" onClick={() => updateEstado(pedido.pedido_id, 'preparando')}>
                            Comenzar preparación
                          </Button>
                        )}
                        {pedido.estado === 'preparando' && (
                          <Button className="w-full bg-green-500 hover:bg-green-600" onClick={() => updateEstado(pedido.pedido_id, 'listo')}>
                            <CheckCircle className="h-4 w-4 mr-2" /> Marcar listo
                          </Button>
                        )}
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </TabsContent>

            <TabsContent value="historial">
              <div className="space-y-3">
                {pedidos.filter(p => ['entregado', 'cancelado', 'en_camino'].includes(p.estado)).slice(0, 20).map((pedido) => (
                  <Card key={pedido.pedido_id}>
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-medium">#{pedido.pedido_id.slice(-6)}</p>
                          <p className="text-xs text-muted-foreground">
                            {new Date(pedido.created_at).toLocaleDateString('es-GT')}
                          </p>
                        </div>
                        <div className="text-right">
                          <Badge className={getStatusColor(pedido.estado)}>{pedido.estado}</Badge>
                          <p className="text-sm font-bold mt-1">{formatMoney(pedido.total)}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </TabsContent>
          </Tabs>

          {/* Quick Links */}
          <div className="mt-6 grid grid-cols-2 lg:grid-cols-4 gap-4">
            <Link to="/merchant/productos">
              <Card className="hover:shadow-md transition-shadow cursor-pointer">
                <CardContent className="p-4 flex items-center gap-3">
                  <ShoppingBag className="h-8 w-8 text-brand-primary" />
                  <div>
                    <p className="font-medium">Productos</p>
                    <p className="text-xs text-muted-foreground">{estadisticas?.productos?.total || 0} total</p>
                  </div>
                  <ChevronRight className="h-5 w-5 ml-auto text-muted-foreground" />
                </CardContent>
              </Card>
            </Link>

            <Link to="/merchant/promociones">
              <Card className="hover:shadow-md transition-shadow cursor-pointer">
                <CardContent className="p-4 flex items-center gap-3">
                  <Tag className="h-8 w-8 text-orange-500" />
                  <div>
                    <p className="font-medium">Promociones</p>
                    <p className="text-xs text-muted-foreground">{estadisticas?.promociones_activas || 0} activas</p>
                  </div>
                  <ChevronRight className="h-5 w-5 ml-auto text-muted-foreground" />
                </CardContent>
              </Card>
            </Link>

            <Link to="/merchant/reviews">
              <Card className="hover:shadow-md transition-shadow cursor-pointer">
                <CardContent className="p-4 flex items-center gap-3">
                  <Star className="h-8 w-8 text-yellow-500" />
                  <div>
                    <p className="font-medium">Reviews</p>
                    <p className="text-xs text-muted-foreground">{estadisticas?.calificacion?.total_reviews || 0} total</p>
                  </div>
                  <ChevronRight className="h-5 w-5 ml-auto text-muted-foreground" />
                </CardContent>
              </Card>
            </Link>

            <Link to="/merchant/ventas">
              <Card className="hover:shadow-md transition-shadow cursor-pointer">
                <CardContent className="p-4 flex items-center gap-3">
                  <FileText className="h-8 w-8 text-blue-500" />
                  <div>
                    <p className="font-medium">Ventas</p>
                    <p className="text-xs text-muted-foreground">Ver historial</p>
                  </div>
                  <ChevronRight className="h-5 w-5 ml-auto text-muted-foreground" />
                </CardContent>
              </Card>
            </Link>
          </div>
        </main>
      </div>

      {/* Modal Aceptar Comisión */}
      <AlertDialog open={showAceptarComision} onOpenChange={setShowAceptarComision}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Aceptar Términos de Comisión</AlertDialogTitle>
            <AlertDialogDescription className="space-y-3">
              <p>Al aceptar, confirmas que estás de acuerdo con:</p>
              <div className="bg-muted p-4 rounded-lg">
                <p><strong>Comisión por venta:</strong> {dashboard?.comercio?.comision_porcentaje}%</p>
                <p><strong>Plan:</strong> {dashboard?.comercio?.plan === 'pro' ? 'Pro' : 'Básico'}</p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={aceptarComision}>Acepto los términos</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default MerchantDashboard;
